// S-1 — private client folders (My Storage › Clients › <client>). Pure logic, no DOM and no window: the rules the UI
// applies before it talks to the server. The server is the authority (S-1 M: spec_vault_folders_guard,
// spec_vault_files_guard, the client/<tree>/ storage policies); these functions mirror it so the app never sends a
// write the server will refuse, and shows the right message instead.

export const CLIENT_PREFIX = 'client/';
const MAX_DEPTH = 32;   // the same bound as vault_client_root_of

const byId = (folders) => (folders instanceof Map ? folders : new Map((folders || []).map(f => [f.id, f])));

// The client folder a folder belongs to: itself, or its nearest kind='client' ancestor. null = not in a client tree.
// Mirrors public.vault_client_root_of.
export function treeOf(folderId, folders) {
  const m = byId(folders);
  let id = folderId, d = 0;
  while (id != null && d <= MAX_DEPTH) {
    const f = m.get(id);
    if (!f) return null;
    if (f.kind === 'client') return f.id;
    id = f.parent_folder_id ?? null; d++;
  }
  return null;
}

export const isManaged = (f) => !!f && (f.kind === 'clients_root' || f.kind === 'client');
export const isFormer = (f) => !!f && f.kind === 'client' && !!f.former_at;

// The storage object path for a new file. Inside a client tree: client/<tree>/<ts>_<name>; elsewhere the existing
// <uid>/<ts>_<name>. spec_vault_files_guard refuses (42501) any other combination.
export function objectPath({ specId, treeId, name, now, safeName }) {
  const safe = (safeName || (s => String(s)))(name);
  const ts = now ?? Date.now();
  return (treeId ? CLIENT_PREFIX + treeId : specId) + '/' + ts + '_' + safe;
}

// Mirrors spec_vault_files_guard's path rule.
export function pathMatchesTree(path, treeId, specId) {
  const p = String(path || '');
  return treeId ? p.startsWith(CLIENT_PREFIX + treeId + '/') : p.startsWith(String(specId) + '/');
}

// Why a rename / move / delete of this folder is refused, as a string key; null = allowed.
// Mirrors spec_vault_folders_guard: system folders can't be renamed or moved; the Clients root is never deleted;
// a client folder can be deleted only once it is a former client's.
export function lockReason(folder, action) {
  if (!folder) return null;
  if (folder.kind === 'clients_root') return 'storage_clients_root_locked';
  if (folder.kind === 'client') {
    if (action === 'delete' && folder.former_at) return null;
    return 'storage_client_folder_locked';
  }
  return null;
}

// The confirm shown before deleting a folder: a former client's folder has its own words.
export function deleteConfirmKeys(folder) {
  return isFormer(folder)
    ? { title: 'storage_delete_former_title', msg: 'storage_delete_former_msg' }
    : { title: 'storage_delete_folder', msg: 'storage_delete_folder_msg' };
}

// Is a write into this place allowed? folderId = the folder the write lands in (null = My Storage root).
// Writes into a client tree need the plan (QF-1); everything else follows today's rules.
export function canWriteInto(folderId, folders, paid) {
  return treeOf(folderId, folders) ? !!paid : true;
}

const pad2 = (n) => String(n).padStart(2, '0');
export function formatDob(dob) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob || ''));
  return m ? m[3] + '.' + m[2] + '.' + m[1] : null;
}
const norm = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();

// QF-8: same-name client folders get the client's date of birth as a suffix; folders without a usable (unique) DOB
// are numbered "(2)", "(3)"… in creation order. If no folder in the group has a DOB suffix, the oldest keeps the
// plain name. dobOf(folder) returns 'YYYY-MM-DD' or null. Returns Map(folderId → display name).
export function displayNames(clientFolders, dobOf) {
  const out = new Map();
  const groups = new Map();
  for (const f of clientFolders || []) {
    const k = norm(f.name);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(f);
  }
  for (const g of groups.values()) {
    if (g.length === 1) { out.set(g[0].id, g[0].name); continue; }
    const dobs = g.map(f => formatDob(dobOf ? dobOf(f) : null));
    const count = new Map();
    dobs.forEach(d => { if (d) count.set(d, (count.get(d) || 0) + 1); });
    const rest = [];
    g.forEach((f, i) => { if (dobs[i] && count.get(dobs[i]) === 1) out.set(f.id, f.name + ' (' + dobs[i] + ')'); else rest.push(f); });
    rest.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')) || String(a.id).localeCompare(String(b.id)));
    const anyDob = rest.length < g.length;
    rest.forEach((f, i) => {
      const n = anyDob ? i + 2 : i + 1;
      out.set(f.id, n === 1 ? f.name : f.name + ' (' + n + ')');
    });
  }
  return out;
}

// The order in My Storage › Clients: current clients by display name, then former clients by display name.
export function sortClientFolders(clientFolders, names) {
  const nm = (f) => norm((names && names.get(f.id)) || f.name || '');
  return [...(clientFolders || [])].sort((a, b) =>
    (isFormer(a) - isFormer(b)) || nm(a).localeCompare(nm(b), undefined, { sensitivity: 'base' }));
}

// S4 (QF-2, QS4-1 A): the account-deletion warning's list. Every client folder of mine (current or former) that holds
// files, with its file count, in the My Storage › Clients order. A file belongs to the folder its storage path names
// (client/<folder id>/…, the prefix the deletion removes). The first `max` are listed; with exactly one more it is
// listed too, so "And {n} more" always has n ≥ 2. → { items: [{ id, label, files }], more, total }
export function deleteWarningList(clientFolders, filePaths, names, max = 10) {
  const count = new Map();
  for (const p of filePaths || []) {
    const m = /^client\/([^/]+)\//.exec(String(p || ''));
    if (m) count.set(m[1], (count.get(m[1]) || 0) + 1);
  }
  const withFiles = (clientFolders || []).filter(f => f.kind === 'client' && count.get(f.id) > 0);
  const sorted = sortClientFolders(withFiles, names);
  const shown = sorted.length <= max + 1 ? sorted : sorted.slice(0, max);
  return {
    items: shown.map(f => ({ id: f.id, label: (names && names.get(f.id)) || f.name, files: count.get(f.id) })),
    more: sorted.length - shown.length,
    total: sorted.length,
  };
}

// The client's folder(s) for a patient context ({kind:'child', child_id} | {kind:'spec_patient', specialist_patient_id}).
// A pre-parent client merged into a connected child can leave two folders (QS1M-2 A): the child's own and the one
// still linked to the pre-parent record (linkedSpId = the sp whose linked_child_id is this child). Both are returned.
export function clientFoldersFor(ctx, folders, linkedSpIds) {
  const all = [...byId(folders).values()].filter(f => f.kind === 'client');
  if (!ctx) return [];
  if (ctx.kind === 'spec_patient') return all.filter(f => f.specialist_patient_id === ctx.specialist_patient_id);
  if (ctx.kind === 'child') {
    const sps = new Set(linkedSpIds || []);
    return all.filter(f => f.child_id === ctx.child_id || (f.specialist_patient_id && sps.has(f.specialist_patient_id)));
  }
  return [];
}
